import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { apiService, ActivityDropdownItem } from "../services/apiService";
import { useApiAuth } from "../hooks/useApiAuth";

interface ActivitiesContextType {
  activities: ActivityDropdownItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const ActivitiesContext = createContext<ActivitiesContextType | undefined>(
  undefined
);

export const ActivitiesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [activities, setActivities] = useState<ActivityDropdownItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isApiReady } = useApiAuth();

  const fetchActivities = useCallback(async () => {
    if (!isApiReady) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiService.getActivitiesDropdown();

      if (response.success && response.data) {
        setActivities(response.data);
      } else {
        setError(response.message || "Failed to load activities");
        setActivities([]);
      }
    } catch (error) {
      console.error("Error fetching activities:", error);
      setError("Network error: Unable to load activities");
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [isApiReady]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const refetch = useCallback(() => {
    fetchActivities();
  }, [fetchActivities]);

  const value = {
    activities,
    loading,
    error,
    refetch,
  };

  return (
    <ActivitiesContext.Provider value={value}>
      {children}
    </ActivitiesContext.Provider>
  );
};

export const useActivitiesContext = (): ActivitiesContextType => {
  const context = useContext(ActivitiesContext);
  if (context === undefined) {
    throw new Error(
      "useActivitiesContext must be used within an ActivitiesProvider"
    );
  }
  return context;
};
